import React from 'react';
import _isEqual from 'lodash/isEqual';
import classNameBind from 'classnames/bind';

import Icon from '../Icon';
import Label from '../Label';
import Popover from '../Popover';
import styles from './Select.module.css';
import { withTranslation } from '../Translator';
import ActionListWrapper from './components/ActionListWrapper';
import { KEYS } from '../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);

const flatten = arr => [].concat(...arr);
const getId = (items, value) => {
  const selectedItem = items.find(item => item.value === value);

  if (selectedItem) {
    return selectedItem.id;
  }

  return null;
};

class Select extends React.PureComponent {
  wrapper = React.createRef();

  static defaultProps = {
    value: null,
    autoFocus: false,
    placeholder: '',
    disabled: false,
    onChange: () => {},
    onBlur: () => {},
    dataId: 'Select',
    showSelectedIcon: false,
    alternativeIcon: {},
    hideTooltip: false,
    type: '',
    emptyListText: undefined,
  }

  constructor(props) {
    super(props);

    this.state = {
      selected: props.value,
    };
  }

  getItemsAsList = () => {
    const { options } = this.props;

    return flatten(
      options.map(elem => elem.items),
    );
  }

  getAvailableItems = () => (
    this.getItemsAsList().filter(item => item.disabled !== true)
  );

  getSelected() {
    const { value } = this.props;
    const selected = this.getItemsAsList()
      .find(item => _isEqual(item.value, value));

    return { name: selected ? selected.name : null, icon: selected ? selected.icon : null };
  }

  handleChange = (newValue) => {
    const { value, onChange, name } = this.props;

    if (!_isEqual(newValue, value)) {
      onChange({
        target: {
          name,
          value: newValue,
        },
      });
      this.setState({
        selected: newValue,
      });
    }
  }

  handleKeyPress = (event) => {
    const { value } = this.props;
    const items = this.getItemsAsList();
    const { selected } = this.state;
    const index = items.findIndex(item => (
      _isEqual(item.value, selected || value)
    ));

    if (event.key === KEYS.ENTER) {
      event.preventDefault();
      this.handleChange(items[index].value);
      if (event.target.form) {
        event.target.form.removeEventListener('keypress', this.handleKeyPress);
      }
    }
  }

  handleKeyDown = (event, onToggle, isOpen) => {
    const { value } = this.props;
    const items = this.getAvailableItems();
    const { selected } = this.state;
    let index = items.findIndex(item => (
      _isEqual(item.value, selected || value)
    ));

    if (event.key === KEYS.TAB && isOpen) {
      onToggle();
    }

    if (event.key === KEYS.SPACE) {
      event.preventDefault();
      this.handleChange(items[index].value);
    }

    if (!event.target.form && event.key === KEYS.ENTER) {
      event.preventDefault();
      this.handleChange(items[index].value);
      onToggle();
    }

    if ([KEYS.DOWN, KEYS.UP].includes(event.key)) {
      event.preventDefault();

      if (!isOpen) {
        if (event.target.form) {
          event.target.form.addEventListener('keypress', this.handleKeyPress);
        }

        return onToggle();
      }

      if (event.key === KEYS.DOWN) {
        index += 1;
      } else if (event.key === KEYS.UP) {
        index -= 1;
      }

      if (this.isInsideTheRange(index, items)) {
        this.setState({
          selected: items[index].value,
        });
      }
    }

    return null;
  }

  handleBlur = (event) => {
    const { onBlur, name, value, dataId } = this.props;

    const popover = document.querySelector(`[data-ppuc-id="${dataId}.popover.expanded"]`);
    if (popover !== null && popover.contains(event.relatedTarget)) {
      return;
    }

    onBlur({
      target: {
        name,
        value,
      },
    });
  }

  handleSelection = (dir) => {
    const { value } = this.props;
    const items = this.getItemsAsList();
    const { selected } = this.state;
    const index = items.findIndex(item => (
      _isEqual(item.value, selected || value)
    )) + dir;

    if (this.isInsideTheRange(index, items)) {
      this.setState({
        selected: items[index].value,
      });
    }
  }

  isInsideTheRange = (index, items) => (
    index >= 0 && index < items.length
  )

  render() {
    const {
      value,
      options,
      disabled,
      onOpen,
      placeholder,
      autoFocus,
      t,
      dataId,
      showSelectedIcon,
      alternativeIcon,
      hideTooltip,
      type,
      error,
      touched,
      emptyListText,
      hideArrowDownIcon,
    } = this.props;
    const {
      selected,
    } = this.state;
    const currentObject = this.getSelected();
    const currentIcon = alternativeIcon.icon || currentObject.icon;
    const showIcon = !!alternativeIcon.icon || showSelectedIcon;
    const zeroStateText = placeholder || t('selectValue');
    const current = (() => {
      if (value === null || value === undefined) {
        return zeroStateText;
      }
      if (currentObject.name === null) {
        return '';
      }

      return currentObject.name;
    })();

    const getLabelType = () => {
      if (type === 'subduedSmall' || type === 'subduedCompactSmall') {
        return 'small';
      }

      return 'default';
    };

    return (
      <div
        className={bindClasses('wrapper', type, {
          disabled,
        })}
        onBlur={!disabled ? this.handleBlur : undefined}
        ref={this.wrapper}
        data-ppuc-id={dataId}
      >
        <Popover
          onOpen={onOpen}
          position="bottom"
          disableFocusStyle={['subduedCompact', 'subduedCompactSmall', 'subduedRounded'].includes(type)}
          activatorElement={(
            <div
              className={bindClasses('select', type, { disabled, error: error && touched })}
              data-ppuc-id={`${dataId}.value`}
            >
              <span className={bindClasses('label', { hideArrowDownIcon })}>
                <Label
                  noSelect
                  title={hideTooltip ? '' : current}
                  type={getLabelType(type)}
                >
                  {currentIcon && showIcon ? (
                    <div className={bindClasses('label-flex')}>
                      <Icon
                        name={currentIcon}
                        className={bindClasses('icon')}
                        color={alternativeIcon.color}
                      />
                      <span className={bindClasses('icon-text-container')}>
                        {current}
                      </span>
                    </div>
                  ) : current}
                </Label>
              </span>
              {hideArrowDownIcon ? null : (
                <Icon
                  name="chevron-down"
                  className={bindClasses('icon')}
                />
              )}
            </div>
          )}
          noPadding
          autoFocus={autoFocus}
          minWidth
          onSelection={this.handleSelection}
          disabled={disabled}
          onKeyDown={this.handleKeyDown}
          dataId={`${dataId}.popover`}
        >
          {close => (
            <ActionListWrapper
              groups={options}
              onItemSelected={(event) => {
                this.handleChange(event.value);
                close();
              }}
              current={getId(this.getAvailableItems(), selected || value)}
              emptyListText={emptyListText}
              // dataId={`${dataId}.options`} TODO: uncomment in PPUCC-378
            />
          )}
        </Popover>
      </div>
    );
  }
}

const TranslatedSelect = withTranslation('components')(Select);

export { TranslatedSelect as Select };
